/**
 * Embed ordering for rich-text post bodies.
 *
 * The API stores a post as one HTML body and appends the author's hashtags to
 * the end of it, so a post written around a YouTube embed comes back as
 * caption → iframe → "#WLB". Rendered verbatim that puts the hashtags under
 * the player, unlike every other post type where the whole caption sits above
 * the media.
 *
 * This moves the embeds to the end of the body — caption and hashtags first,
 * player last — without touching the embed markup itself, so the player keeps
 * working exactly as before. Nothing is duplicated: each embed is relocated,
 * not copied.
 */

/** Elements treated as embedded media. Only iframes survive the sanitizer. */
const EMBED_SELECTOR = 'iframe';

const isLineBreak = (node: Element | null): node is Element =>
  node?.tagName === 'BR';

/** Wrappers left behind by a relocated embed are dropped when nothing remains. */
const isDiscardableWrapper = (element: Element): boolean =>
  element.children.length === 0 &&
  !element.querySelector('img') &&
  !(element.textContent ?? '').trim();

/**
 * Returns the body with every embed appended after the text, or the input
 * unchanged when it holds no embed (the overwhelming majority of posts).
 */
export function moveEmbedsAfterText(sanitizedHtml: string): string {
  if (!sanitizedHtml || !sanitizedHtml.includes('<iframe')) return sanitizedHtml;

  const doc = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
  const body = doc.body;
  const embeds = Array.from(body.querySelectorAll(EMBED_SELECTOR));
  if (!embeds.length) return sanitizedHtml;

  for (const embed of embeds) {
    const parent = embed.parentElement;
    // The line break that separated the embed from the text would otherwise
    // survive as a blank line in the middle of the caption.
    const trailingBreak = isLineBreak(embed.nextElementSibling)
      ? embed.nextElementSibling
      : isLineBreak(embed.previousElementSibling)
        ? embed.previousElementSibling
        : null;
    trailingBreak?.remove();
    embed.remove();
    // A paragraph that only ever held the embed would render as blank space.
    if (parent && parent !== body && isDiscardableWrapper(parent)) {
      parent.remove();
    }
  }

  // Document order is preserved, so a post with several embeds keeps them in
  // the sequence the author wrote.
  embeds.forEach(embed => body.appendChild(embed));

  return body.innerHTML;
}
